import React from 'react';
import PropTypes from 'prop-types';
import act from '@flux/actions/actView';
import { Icon } from '@ts/storybook';

import { LinearProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Toolbar } from '@mui/material';

import { Loading } from '@components/Misc';
import { Select } from '@components/Inputs';
import { GridBreak } from '@components/Utils';
import { withTranslation } from 'react-i18next';
import { Button, Grid, TextInput, Checkbox, Typography } from '@ts/storybook';

class QueryPaginatedSelectionModal extends React.Component {
  state = {
    activePage: 1,
    itemsPerPage: 5,
    quickfind: '',
  };
  _pkColumn = _.find(this.props.columns, c => c.name === this.props.pkColumn);
  _dataPath = this.props.itemsDataPath || 'itemsPage';
  _fPage = false;

  componentDidMount() {
    if (this._pkColumn == null) {
      throw Error(`Cannot find colummn ${this.props.pkColumn}`);
    }
    this.fetchApi();
  }
  componentDidUpdate() {
    if (this._fPage) {
      this.fetchApi();
    }
  }
  fetchApi = () => {
    const { filters: filters0, where: where0, itemsFetchUrl, fetchData } = this.props;
    this._fPage = false;
    act.markAsStale(this._dataPath);
    let where = { ...where0, isAlive: true };
    const filters = { ...filters0, ...(fetchData ? fetchData.filters : null) };

    if (!_.isEmpty(this.state.quickfind)) {
      const like = {
        $or: [{ logicalId: { $iLike: `%${this.state.quickfind}%` } }, { name: { $iLike: `%${this.state.quickfind}%` } }],
      };
      where = _.assign(like, where);
    }

    act.fetch({
      url: itemsFetchUrl,
      dataPath: this._dataPath,
      data: {
        ...fetchData,
        limit: this.state.itemsPerPage,
        offset: (this.state.activePage - 1) * this.state.itemsPerPage,
        findAndCount: true,
        where,
        filters,
      },
    });
  };
  render() {
    if (this.props.data.getIn([this._dataPath, 'state']) != 'IDLE') return <LinearProgress mode="indeterminate" />;

    // Get the data from storage
    this._items_page = this.props.data.getIn([this._dataPath, 'data', 'rows']);
    this._num_rows = this.props.data.getIn([this._dataPath, 'data', 'count']);
    if (this._items_page == null || this._num_rows == null) {
      return <Loading />;
    }

    // data pre-processing
    this._prepareCols();
    // filtering and sorting
    this._rows = this._preprocess(this._items_page);
    // pagination
    return (
      <div
        className="mb-2.5 py-px px-4"
        style={{
          zIndex: this.props.zIndex,
        }}
      >
        <br />
        <SelectionToolBar
          {...this.props}
          quickfind={this.state.quickfind}
          onChangeQuickfind={this._handleChangeQuickFindValue}
          onQuickfindButtonClick={this.fetchApi}
        />
        <div className="overflow-x-auto">
          <Table>
            <SelectionTableHeader
              {...this.props}
              onSelectAllClick={this._handleCheckAllClick}
              checked={this._rows.count() > 0 && this._areAllChecked()}
              disabled={this._rows.count() <= 0}
            />
            <TableBody>{this._rows.map((row, idxRow) => this._renderRow(row, idxRow))}</TableBody>
          </Table>
        </div>
        <Pagination
          {...this.props}
          onNextPage={this._handleNextPage}
          onPrevPage={this._handlePreviousPage}
          onItemsPerPageChange={this._handleItemsPerPageChange}
          numRows={this._num_rows}
          activePage={this.state.activePage}
          itemsPerPage={this.state.itemsPerPage}
        />
      </div>
    );
  }

  _prepareCols() {
    // sets the index of the column in the array as a propery of the column
    _.each(this.props.columns, (column, idx) => {
      column.idx = idx;
    });
  }
  _preprocess(rows) {
    // Adds the colValues array in the row: values that goes to columns
    const newRows = rows.map(row => {
      const colValues = _.map(this.props.columns, column => this._getRawValue(row, column));
      return row.set('_colValues', colValues);
    });
    return newRows;
  }
  _getRawValue(row, column) {
    // Get the value of the row using the function defined in the column
    let fn, ref;
    fn =
      (ref = column.rawValue) != null
        ? ref
        : function (o) {
          return o.get(column.name);
        };
    return fn(row);
  }
  _renderValue(row, column, fnFilter) {
    // Returns the raw value for the column, if the column has a render method
    // calls the render method.
    let fn, rawValue, ref, ref1;
    rawValue = row.get('_colValues')[column.idx];

    if (rawValue == null) {
      return '';
    }

    fn =
      (ref = column.render) != null
        ? ref
        : function () {
          return rawValue;
        };
    return (ref1 = fn(row, rawValue, fnFilter)) != null ? ref1 : '';
  }

  _renderRow(row, idxRow) {
    return (
      <TableRow key={idxRow} hover role="checkbox">
        <TableCell padding="checkbox" className="w-[50px]">
          <Checkbox
            checked={this._isCheckedRow(row)}
            onChange={() => this._handleCheckClick(row)}
            value="check"
            color="primary"
          />
        </TableCell>
        {this.props.columns.map((column, idxCol) => this._renderCell(row, column, idxCol))}
      </TableRow>
    );
  }
  _renderCell(row, column, idxCol) {
    if (column.fHidden) return null;
    return <TableCell key={idxCol}>{this._renderValue(row, column, this.props.fnFilter)}</TableCell>;
  }

  _isCheckedRow(row) {
    return this.props.selected.has(row.get(this.props.columnToRetrieve));
  }

  _handleCheckClick(row) {
    let newSelected;
    let alreadyChecked = this._isCheckedRow(row);
    if (alreadyChecked) {
      //remove from selected
      newSelected = this.props.selected.delete(row.get(this.props.columnToRetrieve));
    } else {
      //add to selected
      newSelected = this.props.selected.set(row.get(this.props.columnToRetrieve), row);
    }
    this.props.onChangeSelected(newSelected);
  }

  _areAllChecked = () => this._rows.reduce((accum, row) => accum && this._isCheckedRow(row), true);

  _handleCheckAllClick = () => {
    let newSelected = this.props.selected;
    if (this._areAllChecked()) {
      // deselect all
      this._rows.forEach(row => {
        newSelected = newSelected.delete(row.get(this.props.columnToRetrieve));
      });
    } else {
      // select all
      this._rows.forEach(row => {
        if (this._isCheckedRow(row) === false) {
          newSelected = newSelected.set(row.get(this.props.columnToRetrieve), row);
        }
      });
    }

    this.props.onChangeSelected(newSelected);
  };

  _handleNextPage = ev => {
    this._fPage = true;
    this.setState({ activePage: this.state.activePage + 1 });
  };
  _handlePreviousPage = ev => {
    this._fPage = true;
    this.setState({ activePage: this.state.activePage - 1 });
  };
  _handleItemsPerPageChange = value => {
    this._fPage = true;
    this.setState({ itemsPerPage: value, activePage: 1 });
  };

  _handleChangeQuickFindValue = value => {
    if (_.isEmpty(value)) this._fPage = true;
    this.setState({ quickfind: value });
  };
  // end class
}

QueryPaginatedSelectionModal.propTypes = {
  app: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  pkColumn: PropTypes.string.isRequired,
  selected: PropTypes.object.isRequired,
  itemsFetchUrl: PropTypes.string.isRequired,
  onChangeSelected: PropTypes.func.isRequired,
  columnToRetrieve: PropTypes.string,
};

QueryPaginatedSelectionModal.defaultProps = {
  columnToRetrieve: 'id',
  fetchData: {},
};

const SelectionToolBar = props => {
  const _style = {
    searchClear: { marginRight: 10 },
  };
  return (
    <Toolbar className="pl-4">
      <Grid container>
        <Typography variant="h6" id="tableTitle" text={props.title} />
        <GridBreak />
        <Grid size={{ xs: 4 }}>
          <TextInput onChangeValue={props.onChangeQuickfind} value={props.quickfind} hintText="Find by id or name" />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Button
            variant="outlined"
            onClick={props.onQuickfindButtonClick}
            disabled={_.isEmpty(props.quickfind)}
            style={_style.searchClear}
            label="Find"
          />
        </Grid>
      </Grid>
    </Toolbar>
  );
};

// let QuickFind = (props) => {
//   return(
//
//   );
// }

class SelectionTableHeader extends React.Component {
  // createSortHandler = property => event => {
  //   this.props.onRequestSort(event, property);
  // };

  render() {
    return (
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              checked={this.props.checked}
              onChange={this.props.onSelectAllClick}
              disabled={this.props.disabled}
              value="checkAll"
              color="primary"
            />
          </TableCell>
          {this.props.columns.map(row => {
            if (row.fHidden) return null;
            return <TableCell key={row.idx}>{row.fnLabel()}</TableCell>;
          }, this)}
        </TableRow>
      </TableHead>
    );
  }
}
SelectionTableHeader.propTypes = {
  onSelectAllClick: PropTypes.func.isRequired,
};

const Pagination = props => {
  const numPages = Math.ceil(props.numRows / props.itemsPerPage);
  const activePage = Math.min(numPages, props.activePage);
  const rowStart = (activePage - 1) * props.itemsPerPage;
  const rowEnd = Math.min(rowStart + props.itemsPerPage, props.numRows);
  let rowStr = props.t('Showing 0 elements');
  if (activePage > 0) {
    rowStr = `${props.t('Showing')} ${rowStart + 1}-${rowEnd} ${props.t('of')} ${props.numRows} ${props.t('elements')}`;
  }
  return (
    <Grid container spacing={1} direction="row" justify="flex-start" alignItems="center">
      <Grid size={{ xs: 'auto' }}>
        <IconButton onClick={props.onPrevPage} disabled={activePage <= 1 || props.numRows == null}>
          <Icon icon="navigation-chevron-left" />
        </IconButton>
        <IconButton onClick={props.onNextPage} disabled={activePage == numPages || props.numRows == null}>
          <Icon icon="navigation-chevron-right" />
        </IconButton>
      </Grid>
      <Grid size={{ xs: 7 }}>{rowStr}</Grid>
      <Grid size={{ xs: 3 }}>
        <ItemsPerPage t={props.t} value={props.itemsPerPage} onChange={props.onItemsPerPageChange} />
      </Grid>
    </Grid>
  );
};

const ItemsPerPage = props => {
  const itemsPerPageMenu = [
    {
      fnValue() {
        return 2;
      },
      fnLabel: () => props.t('2 per page'),
    },
    {
      fnValue() {
        return 5;
      },
      fnLabel: () => props.t('5 per page'),
    },
    {
      fnValue() {
        return 10;
      },
      fnLabel: () => props.t('10 per page'),
    },
    {
      fnValue() {
        return 25;
      },
      fnLabel: () => props.t('25 per page'),
    },
  ];
  return <Select label={props.t('Items per page')} value={props.value} onChange={props.onChange} options={itemsPerPageMenu} />;
};

export default withTranslation()(QueryPaginatedSelectionModal);
